import { useEffect, useState } from 'react'
import { Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button, InlineAlert } from '@chillout/ui'
import { reportUnexpectedError } from '../../../../../lib/reportError'
import { AdminModal, ModalActions } from '../../../shared/Modal'
import { useBranches } from '../useBranches'
import { useUpdateDevice } from './useUpdateDevice'
import { useDeactivateDevice } from './useDeactivateDevice'
import { useRegeneratePairingCode } from './useRegeneratePairingCode'
import { useReactivateDevice } from './useReactivateDevice'
import { DevicePairingCodeCard } from '../DevicePairingCodeCard'
import { EditDeviceForm } from './EditDeviceForm'
import { PairingCodeExpiredCard } from './PairingCodeExpiredCard'
import { updateDeviceSchema, formatPairingCode } from '../deviceSchemas'
import { DeviceNameConflictError, DeviceStateConflictError } from '../deviceErrors'
import type { Device } from '../useDevices'

interface EditDeviceModalProps {
  device: Device | null
  onClose: () => void
}

export function EditDeviceModal({ device, onClose }: EditDeviceModalProps) {
  const { t } = useTranslation()
  const { data: branches = [] } = useBranches()
  const branchOptions = branches.map((b) => ({ label: b.name, value: b.id }))

  const saveMutation = useUpdateDevice()
  const deactivateMutation = useDeactivateDevice()
  const reactivateMutation = useReactivateDevice()
  const regenMutation = useRegeneratePairingCode()

  const [regenResult, setRegenResult] = useState<{ pairingCode: string; expiresAt: Date } | null>(null)
  const [localStatus, setLocalStatus] = useState<'active' | 'inactive' | 'pending' | null>(null)

  const { control, handleSubmit, reset } = useForm({
    resolver: zodResolver(updateDeviceSchema(t)),
    defaultValues: { name: '', branchId: '' },
  })

  useEffect(() => {
    if (device) {
      reset({ name: device.name, branchId: device.branchId })
      saveMutation.reset()
      deactivateMutation.reset()
      reactivateMutation.reset()
      regenMutation.reset()
      setRegenResult(null)
      setLocalStatus(null)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device?.id])

  useEffect(() => {
    if (device?.status && localStatus && device.status !== localStatus) {
      setLocalStatus(null)
      setRegenResult(null)
    }
  }, [device?.status, localStatus])

  const activePairingCode = regenResult?.pairingCode ?? device?.pairingCode ?? ''
  const activePairingExpiresAt = regenResult?.expiresAt ?? device?.pairingCodeExpiresAt ?? null
  const canShowCode = !!activePairingCode && (!activePairingExpiresAt || activePairingExpiresAt > new Date())
  const effectiveStatus = localStatus ?? device?.status

  function handleCopyCode() {
    navigator.clipboard.writeText(activePairingCode).catch(reportUnexpectedError)
  }

  function handleRegenerate() {
    if (!device) return
    regenMutation.mutate(device.id, {
      onSuccess: (result) => setRegenResult(result),
    })
  }

  function handleDeactivate() {
    if (!device) return
    deactivateMutation.mutate(device.id, { onSuccess: onClose })
  }

  function handleReactivate() {
    if (!device) return
    reactivateMutation.mutate(device.id, {
      onSuccess: (result) => {
        setRegenResult(result)
        setLocalStatus('pending')
      },
    })
  }

  const onSubmit = handleSubmit(({ name, branchId }) => {
    if (!device || !effectiveStatus) return
    saveMutation.mutate(
      { id: device.id, name: name.trim(), branchId, status: effectiveStatus },
      { onSuccess: onClose },
    )
  })

  const secondaryAction = effectiveStatus === 'inactive' ? (
    <Button
      label={t('operations.editDevice.reactivateButton')}
      loadingLabel={t('operations.editDevice.reactivatingButton')}
      variant="secondary"
      className="flex-1"
      loading={reactivateMutation.isPending}
      onPress={handleReactivate}
    />
  ) : (
    <Button
      label={t('operations.editDevice.makeInactiveButton')}
      loadingLabel={t('operations.editDevice.makingInactiveButton')}
      variant="secondary"
      className="flex-1"
      loading={deactivateMutation.isPending}
      onPress={handleDeactivate}
    />
  )

  return (
    <AdminModal
      visible={device !== null}
      onClose={onClose}
      closeAccessibilityLabel={t('operations.editDevice.closeAccessibilityLabel')}
      title={t('operations.editDevice.title')}
      subtitle={t('operations.editDevice.subtitle')}
      actions={
        <ModalActions>
          {secondaryAction}
          <Button
            label={t('operations.editDevice.submitButton')}
            loadingLabel={t('operations.editDevice.savingButton')}
            variant="primary"
            className="flex-1"
            loading={saveMutation.isPending}
            onPress={onSubmit}
          />
        </ModalActions>
      }
    >
      <View className="mt-6 gap-5">
        <EditDeviceForm
          control={control}
          branchOptions={branchOptions}
          effectiveStatus={effectiveStatus}
        />
        {effectiveStatus === 'pending' && (
          canShowCode ? (
            <DevicePairingCodeCard
              pairingCode={activePairingCode}
              formattedCode={formatPairingCode(activePairingCode)}
              expiresAt={activePairingExpiresAt}
              copyAccessibilityLabel={t('operations.editDevice.copyCodeAccessibilityLabel')}
              onCopy={handleCopyCode}
            />
          ) : (
            <PairingCodeExpiredCard
              loading={regenMutation.isPending}
              isError={regenMutation.isError}
              onRegenerate={handleRegenerate}
            />
          )
        )}
        {effectiveStatus === 'inactive' && (
          <Text className="text-sm font-normal text-secondary">{t('operations.editDevice.inactiveNote')}</Text>
        )}
        {effectiveStatus === 'active' && (
          <Text className="text-sm font-normal text-secondary">{t('operations.editDevice.activeNote')}</Text>
        )}
        {deactivateMutation.isError && (
          <InlineAlert message={t('operations.editDevice.statusChangeError')} />
        )}
        {reactivateMutation.isError && (
          <InlineAlert message={
            reactivateMutation.error instanceof DeviceNameConflictError
              ? t('operations.editDevice.reactivateNameConflictError')
              : reactivateMutation.error instanceof DeviceStateConflictError
                ? t('operations.editDevice.reactivateStateConflictError')
                : t('operations.editDevice.reactivateError')
          } />
        )}
      </View>
      {saveMutation.isError && (
        <View className="mt-4">
          <InlineAlert message={
            saveMutation.error instanceof DeviceNameConflictError
              ? t('operations.editDevice.nameConflictError')
              : t('operations.editDevice.submitError')
          } />
        </View>
      )}
    </AdminModal>
  )
}
